// src/components/financials/DonorTaxStatements.js
// Updated: 01/25/2026 - Adjusted Letterhead Spacing (Logo Left, Text Center, Distinct Separation)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, MenuItem, Button, 
  Table, TableBody, TableCell, TableHead, TableRow, Divider, 
  Autocomplete, CircularProgress, Alert, TableContainer
} from '@mui/material';
import { PictureAsPdf, ReceiptLong, SwapHoriz } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const DonorTaxStatements = () => {
  const { userProfile } = useAuth();
  const churchId = userProfile?.churchId;
  const printRef = useRef(); 

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const [donors, setDonors] = useState([]); 
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [churchInfo, setChurchInfo] = useState(null);
  const [donorAddress, setDonorAddress] = useState(null);

  const getSafeDate = (dateVal) => {
    if (!dateVal) return "";
    if (typeof dateVal === 'string') return dateVal;
    if (dateVal.seconds) return new Date(dateVal.seconds * 1000).toISOString().split('T')[0];
    return "";
  };

  useEffect(() => {
    if (!churchId) return;
    const fetchMetadata = async () => {
      const churchSnap = await getDoc(doc(db, 'churches', churchId));
      if (churchSnap.exists()) {
          const data = churchSnap.data();
          const lp = data.legalProfile || {};
          setChurchInfo({
              name: lp.legalName || data.name || "Church Name",
              logoUrl: lp.logoUrl || data.logoUrl || "", 
              address: lp.address || data.address || "",
              city: lp.city || data.city || "",
              state: lp.state || data.state || "",
              zip: lp.zip || data.zip || "",
              taxId: lp.taxId || ""
          });
      }
      const unitsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'));
      const memberToUnitMap = {}; 
      const units = unitsSnap.docs.map(d => {
        const data = d.data();
        if (data.memberIds) data.memberIds.forEach(uid => memberToUnitMap[uid] = { id: d.id, name: data.name });
        return { id: d.id, ...data, type: data.isGuest ? 'guest' : 'unit' };
      });
      const usersSnap = await getDocs(query(collection(db, 'users'), where('churchId', '==', churchId)));
      const individuals = usersSnap.docs.map(docSnap => {
        const d = docSnap.data();
        return { id: docSnap.id, name: `${d.firstName} ${d.lastName}`, type: 'individual', belongsToUnit: memberToUnitMap[docSnap.id] || null };
      });
      setDonors([...units, ...individuals].sort((a,b) => a.name.localeCompare(b.name)));
    };
    fetchMetadata();
  }, [churchId]);

  useEffect(() => {
    const fetchAddress = async () => {
      if (!selectedDonor) { setDonorAddress(null); return; }
      let targetUserId = null;
      if (selectedDonor.type === 'guest') { setDonorAddress({ address: selectedDonor.address, city: selectedDonor.city, state: selectedDonor.state, zip: selectedDonor.zip }); return; }
      if (selectedDonor.type === 'individual') { targetUserId = selectedDonor.id; } 
      else if (selectedDonor.type === 'unit') {
        if (selectedDonor.address) { setDonorAddress({ address: selectedDonor.address, city: selectedDonor.city, state: selectedDonor.state, zip: selectedDonor.zip }); return; }
        if (selectedDonor.memberIds?.[0]) targetUserId = selectedDonor.memberIds[0];
      }
      if (targetUserId) {
        try {
          const detailSnap = await getDoc(doc(db, 'users', targetUserId, 'privateContactInfo', 'details'));
          if (detailSnap.exists()) {
            const d = detailSnap.data();
            setDonorAddress({ address: d.address || d.addrStreet || '', city: d.city || d.addrCity || '', state: d.state || d.addrState || '', zip: d.zip || d.addrZip || '' });
          }
        } catch (e) { setDonorAddress(null); }
      }
    };
    fetchAddress();
  }, [selectedDonor]);

  const handleGenerateReport = useCallback(async () => {
    if (!selectedDonor || !churchId) return;
    setLoading(true);
    try {
      let ids = selectedDonor.type === 'unit' ? [...(selectedDonor.memberIds || []), selectedDonor.id] : [selectedDonor.id];
      const q = query(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'), where('donorId', 'in', ids));
      const snap = await getDocs(q);
      const processed = snap.docs
        .map(d => ({ ...d.data(), date: getSafeDate(d.data().date) }))
        .filter(t => t.status === 'posted' && t.date.startsWith(String(year)))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setTransactions(processed);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedDonor, churchId, year]);

  useEffect(() => { handleGenerateReport(); }, [handleGenerateReport]);

  const downloadPDF = async () => {
    const element = printRef.current;
    if (!element) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, allowTaint: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      const pdfBlob = pdf.output('bloburl');
      const newWindow = window.open(pdfBlob, '_blank');
      if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
          alert("Pop-up blocked! Downloading file directly.");
          pdf.save(`Statement_${selectedDonor.name.replace(/\s+/g, '_')}_${year}.pdf`);
      }
    } catch (e) { alert("PDF Error: " + e.message); }
    setDownloading(false);
  };

  const switchToFamily = () => {
      if (selectedDonor?.belongsToUnit) {
          const familyObj = donors.find(d => d.id === selectedDonor.belongsToUnit.id);
          if (familyObj) setSelectedDonor(familyObj);
      }
  };

  const totalGiving = transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  const getDonorLabel = (option) => {
    if (!option) return "";
    if (option.type === 'unit') return `Family: ${option.name}`;
    if (option.type === 'guest') return `Guest: ${option.name}`;
    return option.name;
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper className="no-print" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom><ReceiptLong sx={{ mr: 1, verticalAlign: 'middle' }} /> Statement Lookup</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField select label="Year" fullWidth value={year} onChange={(e) => setYear(e.target.value)}>
              {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={donors}
              value={selectedDonor}
              getOptionLabel={getDonorLabel}
              onChange={(e, val) => setSelectedDonor(val)}
              renderInput={(params) => <TextField {...params} label="Search Donor" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button variant="contained" fullWidth onClick={handleGenerateReport} disabled={!selectedDonor || loading}>
                {loading ? <CircularProgress size={24} /> : "View Statement"}
            </Button>
          </Grid>
        </Grid>
        {selectedDonor?.type === 'individual' && selectedDonor?.belongsToUnit && (
             <Alert severity="info" sx={{ mt: 2 }} action={<Button color="primary" size="small" startIcon={<SwapHoriz />} onClick={switchToFamily}>View {selectedDonor.belongsToUnit.name}</Button>}>
               <strong>Note:</strong> {selectedDonor.name} is part of the <strong>{selectedDonor.belongsToUnit.name}</strong>.
             </Alert>
        )}
      </Paper>

      {transactions.length > 0 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" color="secondary" startIcon={downloading ? <CircularProgress size={20} /> : <PictureAsPdf />} onClick={downloadPDF} disabled={downloading}>
              {downloading ? "Processing..." : "Preview & Download PDF"}
            </Button>
          </Box>

          <Box ref={printRef} sx={{ bgcolor: 'white', p: 0 }}>
            <Paper elevation={0} sx={{ p: '20mm', width: '210mm', minHeight: '297mm', boxSizing: 'border-box', border: '1px solid #eee' }}>
              
              {/* --- HEADER GRID --- */}
              {/* xs=3 Logo (Left), xs=6 Text (Center), xs=3 Spacer (Right) */}
              {/* 'spacing={4}' adds the requested gap between logo and text */}
              <Grid container alignItems="center" spacing={4} sx={{ mb: 2, borderBottom: '1px solid #ccc', pb: 2 }}>
                  
                  {/* LEFT: LOGO */}
                  <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                      {churchInfo?.logoUrl && (
                          <img 
                            src={churchInfo.logoUrl} 
                            alt="Logo" 
                            crossOrigin="anonymous" 
                            style={{ maxHeight: '90px', maxWidth: '100%', objectFit: 'contain' }} 
                          />
                      )}
                  </Grid>

                  {/* CENTER: ADDRESS */}
                  <Grid item xs={6} sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'uppercase', lineHeight: 1.2 }}>
                          {churchInfo?.name}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ lineHeight: 1.1 }}>
                          {churchInfo?.address}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ lineHeight: 1.1 }}>
                          {churchInfo?.city}{churchInfo?.city && ","} {churchInfo?.state} {churchInfo?.zip}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          EIN: {churchInfo?.taxId || "Pending"}
                      </Typography>
                  </Grid>

                  {/* RIGHT: SPACER (Keeps center text perfectly in middle) */}
                  <Grid item xs={3} />
              </Grid>

              {/* --- TITLE & YEAR --- */}
              <Typography variant="h5" align="center" sx={{ mt: 6, mb: 6, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 'bold' }}>
                  Contribution Statement {year}
              </Typography>

              {/* --- DONOR ADDRESS BOX --- */}
              <Box sx={{ mb: 4, p: 2, border: '1px solid #eee', borderRadius: 1, width: '60%' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>{selectedDonor?.name}</Typography>
                {donorAddress && <Typography variant="body1">{donorAddress.address}<br/>{donorAddress.city}, {donorAddress.state} {donorAddress.zip}</Typography>}
              </Box>

              <TableContainer component={Box} sx={{ border: '1px solid #000' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fund</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((t, i) => (
                      <TableRow key={i}><TableCell>{t.date}</TableCell><TableCell>{t.splits?.[0]?.fundName || "General"}</TableCell><TableCell align="right">${(Number(t.amount) || 0).toFixed(2)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, textAlign: 'right', pr: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                      Total: ${totalGiving.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </Typography>
              </Box>

              <Box sx={{ mt: 8, textAlign: 'center', px: 4 }}>
                <Typography variant="caption" fontStyle="italic" display="block">"No goods or services were provided in exchange for these contributions, other than intangible religious benefits."</Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Thank you for your support.</Typography>
              </Box>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DonorTaxStatements;